import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isDefined } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import type { ValidateOrderResponse } from '@/store/orders/state.ts';
import type { ValidateStopLossOrderRequest } from '@/store/orders/stopLoss/state.ts';
import { toBase64 } from '@/utils/toBase64.ts';

export type ValidateStopLossOrderBody = {
  isGtc: boolean;
  expiryDay: string | undefined;
  expiryTime: string | undefined;
  bdrId: string;
  ccyPair: string;
  way: 'buy' | 'sell';
  amountInCcy1: number | undefined;
  amountInCcy2: number | undefined;
  limitPrice?: string;
  base64Emails: string;
  triggerMode: string;
  localTimeWithOffSet: number;
};

export const validateStopLoss = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<ValidateOrderResponse, ValidateStopLossOrderRequest>({
    query: (request) => ({
      url: '/oms/validate/StopLoss',
      method: 'POST',
      body: generateBody(request),
      headers: {
        // without this header, the response is a string
        // instead of JSON, even if the value is "application/json"
        accept: '*/*',
      },
    }),
    transformErrorResponse: (errorResponse): ValidateOrderResponse | FetchBaseQueryError => {
      if (errorResponse.status === 400) {
        if (typeof errorResponse.data === 'string') {
          const data = JSON.parse(errorResponse.data);

          if (typeof data === 'object' && 'validationResponseDetailContent' in data) {
            return data as ValidateOrderResponse;
          }
        }

        return errorResponse.data as ValidateOrderResponse;
      }

      return errorResponse;
    },
  });
};

const generateBody = (order: ValidateStopLossOrderRequest): ValidateStopLossOrderBody => {
  return {
    isGtc: order.expiry.type === 'GoodTillCancelled',
    expiryDay: order.expiry.type === 'ExpiryDate' ? order.expiry.date : undefined,
    expiryTime: order.expiry.type === 'ExpiryDate' ? order.expiry.time : undefined,
    bdrId: String(order.clientId),
    ccyPair: `${order?.currency1}/${order?.currency2}`,
    way: order.way ?? 'buy',
    amountInCcy1: order.notionalCurrencyIndex === 1 ? order.notional : undefined,
    amountInCcy2: order.notionalCurrencyIndex === 2 ? order.notional : undefined,
    limitPrice: isDefined(order.limitPrice) ? String(order.limitPrice) : undefined,
    base64Emails: isDefined(order?.notificationEmails?.emails)
      ? toBase64(order?.notificationEmails.emails.join(';'))
      : '',
    triggerMode: order?.trigger,
    localTimeWithOffSet: order.clockOffset !== undefined ? Date.now() + order.clockOffset : 0,
  };
};
